export default {
  props: {
    campaignID: {
      type: String,
    },
    campaign: {
      type: Object,
    },
    collectiveGoal: {
      type: Object,
    },
    value: {
      type: Object,
    },
    syncedEmailAddresses: {
      type: Array,
    },
    components: {
      type: Array,
    },
    campaignStartDate: {
      type: Date,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    setInterval(() => {
      const { form } = this;
      const isScheduled = [ 'SCHEDULED', 'SCHEDULING', 'ONHOLD', 'DELIVERED', 'ERRORED' ].includes(form.status);
      const now = new Date();
      const sendAt = typeof form.sendAt === 'string' || typeof form.sendAt === 'number' ? new Date(form.sendAt) : form.sendAt;
      const isLessThanFiveMinutes = sendAt && (sendAt.getTime() - now.getTime()) < 300000 && form.status === 'READY';
      this.isScheduled = isScheduled || isLessThanFiveMinutes;
    }, 1000);
  },
  watch: {
    isIndividual() {
      this.$bvModal.show('target-group-change');
    }
  },
  computed: {
    jobsWhere() {
      const { segment } = this.campaign || {};
      const { jobsConnection } = segment || {};
      const { where } = jobsConnection || {};
      if (!where) {
        return null;
      }
      return typeof where === 'string' ? JSON.parse(where) : where;
    },
    contentSharesWhere() {
      const { contentShares } = this.campaign || {};
      if (!Array.isArray(contentShares) || !contentShares.length) {
        return null;
      }
      return { id_in: contentShares.map(({ id }) => id) };
    },
    usedVariables() {
      const { body } = this;
      const stringBody = typeof body === 'string' ? body : (body ? JSON.stringify(body) : null);
      return [ ...new Set((stringBody || '').split('{{').reduce((r, el) => {
        if (!el.includes('}}')) return r;
        const [ variable ] = el.split('}}');
        r.push(variable);
        return r;
      }, [])) ];
    },
    isJob() {
      const keys = this.variables.filter(({ type }) => type === 'JOB').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('JOB')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    isContent() {
      const keys = this.variables.filter(({ type }) => type === 'CONTENT').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('CONTENT')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    areJobs() {
      const { relations } = this.template || {};
      return Array.isArray(relations) && relations.includes('MANY_JOBS');
    },
    arePiecesOfContent() {
      const { relations } = this.template || {};
      return Array.isArray(relations) && relations.includes('MANY_CONTENT');
    },
    variables() {
      return [
        {
          key: 'JOB_TITLE',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Title of the selected job.',
        }, {
          key: 'JOB_SHARE_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'The link of the job users can share. This is generated uniquely for each user.',
        }, {
          key: 'JOB_DESCRIPTION',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected job.',
        }, {
          key: 'JOB_SHARE_TEXT',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the job.',
        }, {
          key: 'JOB_MATCH_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'LinkedIn network page of the user with possible matches',
        }, {
          key: 'CONTENT_TITLE',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Title of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_LINK',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'The link of the piece of content users can share. This is generated uniquely for each user.',
        }, {
          key: 'CONTENT_DESCRIPTION',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_TEXT',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the piece of content.',
        }, {
          key: 'LEADERBOARD_FIRST_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the first prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the second prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the third prize of the leaderboard.',
        }, {
          key: 'FIRST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The first name of the current user on the leaderboard.',
        }, {
          key: 'LAST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The last name of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POSITION',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The position of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POINTS',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The amount of points/tokens the current user has collected.',
        },
      ];
    },
    hourOptions() {
      return Array.from({ length: 24 }, (_, i) => i).map(i => ({ value: i, text: i.toString().padStart(2, '0') }));
    },
    minuteOptions() {
      return [ 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55 ].map(i => ({ value: i, text: i.toString().padStart(2, '0') }));
    },
    componentOptions() {
      const { components } = this;
      const titleMap = {};
      return [ { value: null, text: 'Campaign Start' }, ...components.filter(({ id }) => id !== this.value.id).map(({ id, slackMessage, teamsMessage, emailMessage, event }) => {
        const type = slackMessage ? 'Slack' : teamsMessage ? 'Teams' : emailMessage ? 'Email' : event ? 'Event' : null;
        const details = slackMessage || teamsMessage || emailMessage || event;
        const title = details ? details.title : null;
        if (title && !titleMap[title]) {
          titleMap[title] = 1;
        } else if (title) {
          titleMap[title] += 1;
        }
        const endNumber = parseInt(`${titleMap[title]}`.split('').pop());
        const appendToNumber = endNumber === 1 ? 'st' : endNumber === 2 ? 'nd' : endNumber === 3 ? 'rd' : 'th';
        return {
          value: id,
          text: `${endNumber}${appendToNumber} ${title} (${type})`,
        };
      }) ];
    },
    sendAt() {
      const { atHour: atHourString, atMinute: atMinuteString } = this.relativeSendAtForm || {};
      const atHour = atHourString ? parseInt(atHourString) : 0;
      const atMinute = atMinuteString ? parseInt(atMinuteString) : 0;
      const message = this.value ? this.value.teamsMessage || this.value.slackMessage || this.value.emailMessage : {};
      const sendAt = message ? (message.sendAt instanceof Date ? message.sendAt : new Date(message.sendAt)) : null;
      sendAt.setHours(atHour);
      sendAt.setMinutes(atMinute);
      return sendAt;
    },
    isSceduledInPast() {
      const { sendAt } = this;
      if (!sendAt) return false;
      return sendAt.getTime() < new Date().getTime() + 15 * 60 * 1000;
    },
    sendAtText() {
      const { sendAt, isSceduledInPast } = this;
      if (!sendAt) return {
        text: 'We are unable to determine the time and date the message will be sent. Please ensure that the campaign\'s start date is set, and verify that the previous message is scheduled correctly',
        error: true,
      };
      if (isSceduledInPast) return {
        text: 'The message is scheduled to be sent in the past. Please ensure the message is scheduled at least 15 minutes in the future. You can adjust the date by changing the dates between this and the following component on the left hand side.',
        error: true,
      };

      const days = [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ];
      const months = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ];

      const dayOfWeek = days[sendAt.getDay()];
      const day = sendAt.getDate();
      const month = months[sendAt.getMonth()];
      const year = sendAt.getFullYear();

      let suffix = 'th';
      if (day % 10 === 1 && day !== 11) {
        suffix = 'st';
      } else if (day % 10 === 2 && day !== 12) {
        suffix = 'nd';
      } else if (day % 10 === 3 && day !== 13) {
        suffix = 'rd';
      }

      return {
        text: `on <b>${dayOfWeek}</b> the <b>${day}${suffix}</b> of ${month}, ${year}`,
        error: false,
      };
    }
  },
  methods: {
    input(key) {
      if (this.isSceduledInPast) return;
      const { form, inputForm, relativeSendAtForm, relativeSendAt } = this;
      if (relativeSendAtForm) {
        delete relativeSendAtForm.__typename;
      }
      if (relativeSendAt) {
        delete relativeSendAt.__typename;
      }
      if (inputForm.job) {
        delete inputForm.job.customFields;
        delete inputForm.job.location;
        delete inputForm.job.createdAt;
        delete inputForm.job.status;
        delete inputForm.job.department;
        delete inputForm.job.__typename;
      }
      if (Array.isArray(inputForm.jobs)) {
        for (let i = 0; i < inputForm.jobs.length; i++) {
          if (inputForm.jobs[i].customFields && Array.isArray(inputForm.jobs[i].customFields) && inputForm.jobs[i].customFields.find(({ key, value }) => key === 'location' && value)) {
            inputForm.jobs[i].title = `${inputForm.jobs[i].title} - ${inputForm.jobs[i].customFields.find(({ key, value }) => key === 'location' && value).value}`;
          }
          delete inputForm.jobs[i].customFields;
          delete inputForm.jobs[i].location;
          delete inputForm.jobs[i].createdAt;
          delete inputForm.jobs[i].status;
          delete inputForm.jobs[i].department;
          delete inputForm.jobs[i].__typename;
        }
      }
      if (inputForm.contentShare) {
        delete inputForm.contentShare.createdAt;
        delete inputForm.contentShare.status;
        delete inputForm.contentShare.atsID;
        delete inputForm.contentShare.url;
        delete inputForm.contentShare.domain;
        delete inputForm.contentShare.imageURL;
        delete inputForm.contentShare.image;
        delete inputForm.contentShare.__typename;
      }
      if (Array.isArray(inputForm.contentShares)) {
        for (let y = 0; y < inputForm.contentShares.length; y++) {
          delete inputForm.contentShares[y].createdAt;
          delete inputForm.contentShares[y].status;
          delete inputForm.contentShares[y].atsID;
          delete inputForm.contentShares[y].url;
          delete inputForm.contentShares[y].domain;
          delete inputForm.contentShares[y].imageURL;
          delete inputForm.contentShares[y].image;
          delete inputForm.contentShares[y].__typename;
        }
      }
      const { job, contentShare, jobs, contentShares, ...rest } = inputForm;
      const { atHour, atMinute } = relativeSendAtForm || {};

      if ((atHour !== null || atHour !== undefined) && typeof atHour === 'string') {
        relativeSendAtForm.atHour = parseInt(relativeSendAtForm.atHour);
      }
      if ((atMinute !== null || atMinute !== undefined) && typeof atMinute === 'string') {
        relativeSendAtForm.atMinute = parseInt(relativeSendAtForm.atMinute);
      }

      this.$emit('input', {
        [key]: {
          template: this.templateID,
          templateDefinition: JSON.stringify(this.template),
          ...form,
          ...(job ? { job } : {}),
          ...(contentShare ? { contentShare } : {}),
          ...(Array.isArray(jobs) ? { jobs } : {}),
          ...(Array.isArray(contentShares) ? { contentShares } : {}),
          target: this.isIndividual ? 'INDIVIDUAL' : 'GROUP',
          replacedBody: this.body,
          relativeSendAt: {
            ...relativeSendAt,
            ...relativeSendAtForm,
          },
          sendAt: this.sendAt,
          inputs: {
            ...(Object.keys(rest).reduce((acc, key) => {
              if (rest[key] && typeof rest[key] === 'object') {
                acc[key] = JSON.stringify(rest[key]);
              } else {
                acc[key] = rest[key];
              }
              return acc;
            }, {})),
          }
        } });
    },
  }
};



// WEBPACK FOOTER //
// ./src/mixins/updateMessage.js